/**
 * Shared primitives for the console.
 *
 * Two ideas run through everything here. First: summary before detail — a
 * moderator arriving at a screen should be able to answer "what needs me now?"
 * before reading a single row. Second: state is encoded in form as well as
 * number — an overdue case counts *up* with a minus sign and a flag, a breached
 * tile is outlined and corner-flagged, a severity stripe carries its rank in the
 * count of its ticks. Nothing depends on hue alone.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { CLUSTERS } from '@alap/shared';

/* ------------------------------------------------------------------ *
 * Time
 * ------------------------------------------------------------------ */

/** One interval per screen, shared by every countdown on it. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "2d 03h" · "3h 12m 08s" · "12m 08s" · "48s" — always monospace-stable. */
export function formatDuration(ms: number): string {
  const total = Math.floor(Math.abs(ms) / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${pad2(hours)}h ${pad2(mins)}m`;
  if (hours > 0) return `${hours}h ${pad2(mins)}m ${pad2(secs)}s`;
  if (mins > 0) return `${mins}m ${pad2(secs)}s`;
  return `${secs}s`;
}

export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (sameDay) return `today ${time}`;
  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${time}`;
}

export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/* ------------------------------------------------------------------ *
 * Severity — derived from the §08 SLA table, which is the real ranking
 * ------------------------------------------------------------------ */

export type Severity = 'critical' | 'high' | 'elevated' | 'routine';

export const SEVERITIES: Severity[] = ['critical', 'high', 'elevated', 'routine'];

/**
 * The SLA *is* the severity. Suspected minor (60m) and NCII (120m) are
 * critical; coercion and harassment (240m) are high; solicitation and catfish
 * (720m) elevated; spam, other and undisclosed monogamy sit at a day or more.
 */
export function severityFor(slaMins: number): Severity {
  if (slaMins <= 120) return 'critical';
  if (slaMins <= 240) return 'high';
  if (slaMins <= 720) return 'elevated';
  return 'routine';
}

export const SEVERITY_TICKS: Record<Severity, number> = {
  critical: 4,
  high: 3,
  elevated: 2,
  routine: 1,
};

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  elevated: 'Elevated',
  routine: 'Routine',
};

export function SeverityStripe({ severity }: { severity: Severity }) {
  const on = SEVERITY_TICKS[severity];
  return (
    <div
      className={`stripe sev-${severity}`}
      role="img"
      aria-label={`${SEVERITY_LABEL[severity]} severity`}
    >
      {[0, 1, 2, 3].map((i) => (
        <i key={i} className={i < on ? 'on' : undefined} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Countdown
 * ------------------------------------------------------------------ */

const ONE_HOUR = 60 * 60 * 1000;

export function Countdown({
  dueAt,
  now,
  slaLabel,
  live,
}: {
  dueAt: string;
  now: number;
  slaLabel: string;
  /** False for closed cases — a closed case has no clock left to run. */
  live: boolean;
}) {
  const due = Date.parse(dueAt);
  if (Number.isNaN(due)) {
    return (
      <div className="countdown">
        <div className="clock">—</div>
        <div className="qualifier">no deadline on record</div>
      </div>
    );
  }

  const remaining = due - now;
  const breached = live && remaining <= 0;
  const soon = live && remaining > 0 && remaining < ONE_HOUR;
  const cls = breached ? 'is-breached' : soon ? 'is-soon' : '';

  if (!live) {
    return (
      <div className="countdown">
        <div className="clock" style={{ fontSize: 15 }}>
          closed
        </div>
        <div className="qualifier">was due {formatTimestamp(dueAt)}</div>
      </div>
    );
  }

  return (
    <div className={`countdown ${cls}`}>
      <div className="clock num" aria-label={breached ? 'overdue by' : 'time remaining'}>
        {formatDuration(remaining)}
      </div>
      <div className="qualifier">
        {breached ? 'past the SLA' : 'left'} · target {slaLabel} · due {formatTimestamp(dueAt)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Sparkline — hand-rolled, no chart library
 * ------------------------------------------------------------------ */

function useMeasuredWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(280);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (w: number) => {
      if (w > 0) setWidth(Math.round(w));
    };
    measure(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) measure(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

export interface SparklineProps {
  /** Chronological readings. One point is a legitimate state, not an error. */
  values: number[];
  /** Drawn as a dashed rule so the series can be read against its own limit. */
  threshold?: number;
  thresholdLabel?: string;
  height?: number;
  /** Screen-reader description; the visual is decorative on its own. */
  label: string;
}

export function Sparkline({
  values,
  threshold,
  thresholdLabel,
  height = 46,
  label,
}: SparklineProps) {
  const [wrapRef, width] = useMeasuredWidth<HTMLDivElement>();
  const gradientId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const padTop = 5;
  const padBottom = 5;
  const padLeft = 1;
  const padRight = 7;
  const innerW = Math.max(20, width - padLeft - padRight);
  const innerH = Math.max(10, height - padTop - padBottom);

  const finite = values.filter((v) => Number.isFinite(v));
  const domainValues = threshold === undefined ? finite : [...finite, threshold];
  let lo = domainValues.length > 0 ? Math.min(...domainValues) : 0;
  let hi = domainValues.length > 0 ? Math.max(...domainValues) : 1;
  if (hi - lo < 1e-9) {
    const nudge = Math.abs(hi) > 1e-9 ? Math.abs(hi) * 0.25 : 0.5;
    lo -= nudge;
    hi += nudge;
  } else {
    const padding = (hi - lo) * 0.14;
    lo -= padding;
    hi += padding;
  }

  const yFor = (v: number): number => padTop + innerH - ((v - lo) / (hi - lo)) * innerH;
  const xFor = (i: number): number =>
    finite.length <= 1 ? padLeft + innerW : padLeft + (i / (finite.length - 1)) * innerW;

  const baseline = padTop + innerH;
  const points = finite.map((v, i) => ({ x: xFor(i), y: yFor(v) }));
  const last = points[points.length - 1];

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  const first = points[0];
  const areaPath =
    points.length > 1 && first && last
      ? `${linePath} L${last.x.toFixed(2)},${baseline} L${first.x.toFixed(2)},${baseline} Z`
      : '';

  const thresholdY = threshold === undefined ? null : yFor(threshold);

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <svg
        className="spark"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
      >
        <defs>
          <linearGradient id={`sparkfill-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--spark)" stopOpacity="0.30" />
            <stop offset="100%" stopColor="var(--spark)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* faint grid — enough to read a slope against, not enough to look at */}
        <g stroke="var(--line)" strokeWidth="1" opacity="0.55">
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={`h${f}`}
              x1={padLeft}
              x2={padLeft + innerW}
              y1={padTop + innerH * f}
              y2={padTop + innerH * f}
            />
          ))}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => (
            <line
              key={`v${f}`}
              y1={padTop}
              y2={padTop + innerH}
              x1={padLeft + innerW * f}
              x2={padLeft + innerW * f}
            />
          ))}
        </g>

        {areaPath && <path d={areaPath} fill={`url(#sparkfill-${gradientId})`} />}

        {thresholdY !== null && (
          <g>
            <line
              x1={padLeft}
              x2={padLeft + innerW}
              y1={thresholdY}
              y2={thresholdY}
              stroke="var(--alta)"
              strokeWidth="1.25"
              strokeDasharray="4 3"
              opacity="0.85"
            />
            {thresholdLabel && (
              <text
                x={padLeft + 3}
                y={Math.max(9, thresholdY - 3)}
                fill="var(--alta)"
                fontSize="9"
                fontFamily="var(--font-mono)"
                opacity="0.95"
              >
                {thresholdLabel}
              </text>
            )}
          </g>
        )}

        {points.length > 1 && (
          <path
            d={linePath}
            fill="none"
            stroke="var(--spark)"
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* the emphasised endpoint: where the number is now */}
        {last && (
          <g>
            <circle cx={last.x} cy={last.y} r="5" fill="var(--spark)" opacity="0.18" />
            <circle
              cx={last.x}
              cy={last.y}
              r="2.6"
              fill="var(--spark)"
              stroke="var(--surface)"
              strokeWidth="1.2"
            />
          </g>
        )}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Loading / empty / error
 * ------------------------------------------------------------------ */

export function Skeletons({ count = 3, height = 92 }: { count?: number; height?: number }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton skeleton-row" style={{ height }} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function ErrorState({
  title,
  message,
  code,
  onRetry,
}: {
  title: string;
  message: string;
  code?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state is-error" role="alert">
      <h3>{title}</h3>
      <p>{message}</p>
      {code && <span className="state-code">{code}</span>}
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Small shared bits
 * ------------------------------------------------------------------ */

export function clusterName(clusterId: string | null | undefined): string {
  if (!clusterId) return 'no cluster';
  return CLUSTERS.find((c) => c.id === clusterId)?.name ?? clusterId;
}

export function clusterCity(clusterId: string): string | null {
  return CLUSTERS.find((c) => c.id === clusterId)?.city ?? null;
}

/** Short, non-identifying handle for a UUID. Enough to cross-reference a log. */
export function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

const ACCOUNT_TONE: Record<string, string> = {
  active: 'chip-river',
  waitlisted: 'chip-indigo',
  suspended: 'chip-brass',
  banned: 'chip-alta',
  deleted: 'chip-alta',
};

export function AccountChip({ status }: { status: string }) {
  return <span className={`chip ${ACCOUNT_TONE[status] ?? ''}`}>{status.replace(/_/g, ' ')}</span>;
}

/** Safety scores arrive as free-form JSON; only show what is actually numeric. */
export function SafetyScoreChips({ scores }: { scores: Record<string, number> | null }) {
  if (!scores || typeof scores !== 'object') return null;
  const entries = Object.entries(scores)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <span className="chip-row">
      {entries.map(([k, v]) => (
        <span key={k} className={`chip chip-mono ${v >= 3 ? 'chip-alta' : 'chip-brass'}`}>
          {k.replace(/_/g, ' ')} {v}
        </span>
      ))}
    </span>
  );
}

/**
 * The Azure triage block. It is rendered the same way everywhere and it always
 * says the same thing: this is advice, a human decides (§08). It is deliberately
 * styled as a quotation rather than a control — there is no button on it,
 * because there is no such thing as "accept the model's verdict" in this tool.
 */
export function AdvisoryBlock({
  summary,
  suggestedVerdict,
  confidence,
  source,
}: {
  summary: string;
  suggestedVerdict: string;
  confidence: number;
  source: string;
}) {
  const pct = Math.round(Math.max(0, Math.min(1, confidence)) * 100);
  return (
    <div className="advisory">
      <div className="advisory-head">
        <span className="advisory-tag">Advisory · automated triage</span>
        <span className="suggested">suggests: {suggestedVerdict.replace(/_/g, ' ')}</span>
        <span className="confidence">
          confidence
          <span className="track">
            <span className="fill" style={{ width: `${pct}%` }} />
          </span>
          <span className="num">{pct}%</span>
        </span>
        <span className="chip chip-mono">
          {source === 'azure_foundry' ? 'azure foundry' : 'offline heuristic'}
        </span>
      </div>
      <p>{summary}</p>
      <span className="decides">
        The moderator decides. Nothing here is applied automatically (§08).
      </span>
    </div>
  );
}
